import { useCallback, useContext } from 'react';
import { FilterContext } from '../FilterProvider';
import { useFilterV2 } from '../../../hooks/useFilterV2';
import {
  determineYearToUse,
  getStartOfMonth,
  getEndOfMonth,
  getCurrentDayOfMonth,
} from '../helper';

// Filter IDs for UTC date sync - must match FrequencyFilter.js
const UTC_DATE_FILTER_IDS = {
  START_UTC_DATE: 'startUTCDate',
  END_UTC_DATE: 'endUTCDate',
};

const isCurrentDate = (date) => {
  if (!date) {
    return false;
  }
  const today = new Date();
  return (
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const getEndDateFn = (date, isShipmentVisibility) => {
  return isShipmentVisibility && isCurrentDate(date)
    ? getCurrentDayOfMonth()
    : getEndOfMonth(date);
};

export const useFrequencyDateLogic = ({ goLiveYear, goLiveMonth }) => {
  const {
    setYTDCheckbox,
    setSelectedYear,
    setSelectedDate,
    setSelectedStartDate,
    setStartUTCDate,
    setSelectedEndDate,
    setEndUTCDate,
  } = useContext(FilterContext);

  // Redux filter management for UTC date sync
  const { updateFilterValue, updateFilterLoading } = useFilterV2();

  const setDateRangeAndUTC = useCallback(
    (startDate, endDate, isShipmentVisibility) => {
      const getEndDate = (date) => getEndDateFn(date, isShipmentVisibility);

      // Set loading state before updating
      updateFilterLoading(UTC_DATE_FILTER_IDS.START_UTC_DATE, true);
      updateFilterLoading(UTC_DATE_FILTER_IDS.END_UTC_DATE, true);

      // Calculate UTC date strings
      const startUTCDateString = getStartOfMonth(startDate)?.toISOString();
      const endOfMonth = getEndDate(endDate);
      const endUTCDateString = endOfMonth?.toISOString();

      // Update FilterContext
      setSelectedStartDate(startDate);
      setStartUTCDate(startUTCDateString);
      setSelectedEndDate(endOfMonth);
      setEndUTCDate(endUTCDateString);

      // Sync to Redux
      updateFilterValue(UTC_DATE_FILTER_IDS.START_UTC_DATE, startUTCDateString);
      updateFilterValue(UTC_DATE_FILTER_IDS.END_UTC_DATE, endUTCDateString);

      // Clear loading state
      updateFilterLoading(UTC_DATE_FILTER_IDS.START_UTC_DATE, false);
      updateFilterLoading(UTC_DATE_FILTER_IDS.END_UTC_DATE, false);
    },
    [
      setSelectedStartDate,
      setStartUTCDate,
      setSelectedEndDate,
      setEndUTCDate,
      updateFilterValue,
      updateFilterLoading,
    ]
  );

  const handleYtdModeOn = useCallback(
    (params) => {
      const { selectedYear, currentYear, currentMonth, isShipmentVisibility } =
        params;

      const yearToUse = determineYearToUse(selectedYear, goLiveYear);
      const isCurrentYear = yearToUse === currentYear;
      const newDate = new Date(yearToUse, isCurrentYear ? currentMonth : 11, 1);
      const startOfYear = new Date(yearToUse, 0, 1);

      setSelectedYear(yearToUse);
      setSelectedDate(newDate);
      setYTDCheckbox(true);

      setDateRangeAndUTC(startOfYear, newDate, isShipmentVisibility);
    },
    [
      goLiveYear,
      setSelectedYear,
      setSelectedDate,
      setYTDCheckbox,
      setDateRangeAndUTC,
    ]
  );

  const handleYtdModeOff = useCallback(
    (params) => {
      const { currentYear, currentMonth, isShipmentVisibility } = params;

      if (goLiveYear === undefined || goLiveMonth === undefined) {
        return;
      }

      setYTDCheckbox(false);

      const currentDate = new Date(currentYear, currentMonth, 1);
      setSelectedYear(currentYear);
      setSelectedDate(currentDate);

      setDateRangeAndUTC(currentDate, currentDate, isShipmentVisibility);
    },
    [
      goLiveYear,
      goLiveMonth,
      setYTDCheckbox,
      setSelectedYear,
      setSelectedDate,
      setDateRangeAndUTC,
    ]
  );

  return {
    handleYtdModeOn,
    handleYtdModeOff,
    setDateRangeAndUTC,
    getEndDateFn,
  };
};
